// src/budget/model/workshop/buildBudgetWorkshopUtils.js

/**
 * Shared utilities for "Build a Budget" style workshops (Town + BOE).
 * Keep this file framework-agnostic: pure functions only.
 */

/**
 * Lightweight mill-rate formatter.
 * @param {number|null|undefined} v
 */
export function fmtMillRate(v) {
  if (!Number.isFinite(v)) return "—";
  return v.toFixed(2);
}

/**
 * Dollar formatter (0 decimals)
 * @param {number|null|undefined} v
 */
export function fmtDollars0(v) {
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/**
 * For displaying mill rate delta next to the value.
 * Caller decides on +/- and parentheses.
 * @param {number|null|undefined} v
 */
export function fmtMillRateDelta(v) {
  if (!Number.isFinite(v)) return null;
  return v.toFixed(2);
}

/**
 * Pull the latest stored Current Levy from the Tax Revenues doc.
 * Prefers:
 *  1) Latest YEAR with Actual -> ["Tax Revenues"]["Current Levy"]
 *  2) Else Latest YEAR with Approved -> ["Tax Revenues"]["Current Levy"]
 */
export function pickLatestCurrentLevy(docData) {
  if (!docData || typeof docData !== "object") return null;

  const YEAR_KEY_RE = /^\d{4}$/;

  const years = Object.keys(docData)
    .filter((k) => YEAR_KEY_RE.test(k))
    .map((k) => Number(k))
    .filter(Number.isFinite)
    .sort((a, b) => b - a); // DESC

  const tryYear = (y) => {
    const yObj = docData?.[String(y)];
    if (!yObj || typeof yObj !== "object") return null;

    const statuses = ["Actual", "Approved"];

    for (const status of statuses) {
      const v = yObj?.[status]?.["Tax Revenues"]?.["Current Levy"];
      if (typeof v === "number" && Number.isFinite(v)) return v;
    }

    return null;
  };

  for (const y of years) {
    const v = tryYear(y);
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }

  return null;
}

/**
 * Ensures every numeric data row has a buildBudget value.
 * Default: buildBudget mirrors y0_proposed if present, else y0_adopted.
 * TOTAL/subtotal rows are left undefined so totals can be recomputed every time.
 *
 * @param {Array<Object>} rows
 * @returns {Array<Object>}
 */
export function seedBuildBudget(rows) {
  const isSubtotalRow = (r) =>
    r?.rowType === "subtotal" ||
    (typeof r?.label === "string" && r.label.trim() === "");

  const isTotalLabelRow = (r) =>
    typeof r?.label === "string" &&
    r.label.trim().toUpperCase().startsWith("TOTAL");

  const isTotalLikeRow = (r) => isSubtotalRow(r) || isTotalLabelRow(r);

  return (rows || []).map((r) => {
    if (r?.section || r?.isSpacerRow || r?.isAnnotationRow || r?.isFooterRow) {
      return r;
    }

    // Never seed totals/subtotals — totals are recomputed
    if (isTotalLikeRow(r)) {
      return { ...r, buildBudget: undefined };
    }

    const hasBuild =
      typeof r?.buildBudget === "number" && Number.isFinite(r.buildBudget);
    if (hasBuild) return r;

    const proposed = r?.y0_proposed;
    const adopted = r?.y0_adopted;

    const proposedOk =
      typeof proposed === "number" && Number.isFinite(proposed);
    const adoptedOk = typeof adopted === "number" && Number.isFinite(adopted);

    if (proposedOk) return { ...r, buildBudget: proposed };
    if (adoptedOk) return { ...r, buildBudget: adopted };

    return r;
  });
}

/**
 * Populate buildBudget on TOTAL rows by summing the rows above them in the same section.
 * Also populates grand totals by summing section totals.
 *
 * Defaults assume your "TOTAL ..." label convention, plus intergov subtotal style rows:
 * - subtotal rows: rowType === "subtotal" OR label === ""
 * - total rows: label starts with "TOTAL"
 *
 * @param {Array<Object>} rows
 * @param {Object} opts
 * @param {string[]} [opts.grandTotalLabels] - labels for grand-total rows to overwrite
 * @returns {Array<Object>}
 */
export function populateBuildBudgetTotals(
  rows,
  opts = { grandTotalLabels: [] }
) {
  const out = (rows || []).map((r) => ({ ...r }));

  const isSkippable = (r) =>
    !r || r.section || r.isSpacerRow || r.isAnnotationRow || r.isFooterRow;

  const isSubtotalRow = (r) =>
    r?.rowType === "subtotal" ||
    (typeof r?.label === "string" && r.label.trim() === "");

  const isTotalLabelRow = (r) =>
    typeof r?.label === "string" &&
    r.label.trim().toUpperCase().startsWith("TOTAL");

  const isTotalLikeRow = (r) => isTotalLabelRow(r) || isSubtotalRow(r);

  // 1) Per-section totals
  let runningSum = 0;
  let sawAny = false;

  for (let i = 0; i < out.length; i += 1) {
    const r = out[i];

    if (isSkippable(r)) {
      if (r?.section) {
        runningSum = 0;
        sawAny = false;
      }
      continue;
    }

    if (isTotalLikeRow(r)) {
      if (sawAny) {
        out[i].buildBudget = runningSum;
      } else if (
        typeof r?.y0_adopted === "number" &&
        Number.isFinite(r.y0_adopted)
      ) {
        // fallback for empty sections
        out[i].buildBudget = r.y0_adopted;
      } else {
        out[i].buildBudget = 0;
      }

      runningSum = 0;
      sawAny = false;
      continue;
    }

    if (typeof r.buildBudget === "number" && Number.isFinite(r.buildBudget)) {
      runningSum += r.buildBudget;
      sawAny = true;
    }
  }

  // 2) Grand totals: sum section totals rows
  const grandLabels = new Set(
    (opts?.grandTotalLabels || []).map((s) => String(s).trim().toUpperCase())
  );

  const sectionTotalsSum = out.reduce((acc, r) => {
    const label = (r?.label || "").trim().toUpperCase();
    if (grandLabels.has(label)) return acc;
    if (!isTotalLikeRow(r)) return acc;

    if (typeof r?.buildBudget === "number" && Number.isFinite(r.buildBudget)) {
      return acc + r.buildBudget;
    }

    return acc;
  }, 0);

  for (let i = 0; i < out.length; i += 1) {
    const label = (out[i]?.label || "").trim().toUpperCase();
    if (grandLabels.has(label)) {
      out[i].buildBudget = sectionTotalsSum;
    }
  }

  return out;
}

/**
 * Computes Change / % Change based on:
 *   Change = buildBudget - y0_adopted
 *   %Change = (Change / y0_adopted) * 100
 *
 * Writes into:
 *   increase, percentIncrease
 *
 * @param {Array<Object>} rows
 */
export function applyBuildBudgetDelta(rows) {
  return (rows || []).map((r) => {
    if (r?.section || r?.isSpacerRow) return r;

    const base = r?.y0_adopted;
    const build = r?.buildBudget;

    const baseOk = typeof base === "number" && Number.isFinite(base);
    const buildOk = typeof build === "number" && Number.isFinite(build);

    if (!baseOk || !buildOk) {
      return { ...r, increase: null, percentIncrease: null };
    }

    const increase = build - base;
    const percentIncrease = base !== 0 ? (increase / base) * 100 : null;

    return { ...r, increase, percentIncrease };
  });
}

/**
 * Compute totals across specific "TOTAL ..." rows.
 * Used for computing overall delta displayed in the workshop header.
 *
 * @param {Array<Object>} allRows - usually a concatenation of multiple tables/sections
 * @param {string[]} totalLabels - labels to include (case-insensitive)
 */
export function computeWorkshopTotals(allRows, totalLabels) {
  const want = new Set((totalLabels || []).map((s) => String(s).toUpperCase()));

  const totalRows = (allRows || []).filter((row) => {
    const label = (row?.label || "").toUpperCase();
    return want.has(label);
  });

  const sumKey = (key) =>
    totalRows.reduce(
      (acc, r) => (typeof r?.[key] === "number" ? acc + r[key] : acc),
      0
    );

  const baseTotal = sumKey("y0_adopted");
  const buildTotal = sumKey("buildBudget");
  const change = buildTotal - baseTotal;
  const pct = baseTotal !== 0 ? (change / baseTotal) * 100 : null;

  return { baseTotal, buildTotal, change, pct };
}

/**
 * Levy behavior:
 * - Start with stored levy
 * - Apply budget delta
 */
export function computeUpdatedLevy(storedLevy, budgetDelta) {
  const baseOk = typeof storedLevy === "number" && Number.isFinite(storedLevy);
  if (!baseOk) return null;

  const deltaOk =
    typeof budgetDelta === "number" && Number.isFinite(budgetDelta);

  return deltaOk ? storedLevy + budgetDelta : storedLevy;
}

/**
 * Mill rate is dollars per $1,000 of assessed value:
 *   millRate = levy / (grandList / 1000) = (levy * 1000) / grandList
 */
export function computeMillRateFromLevyAndGrandList(levy, netGrandList) {
  if (
    !Number.isFinite(levy) ||
    !Number.isFinite(netGrandList) ||
    netGrandList === 0
  ) {
    return null;
  }
  return (levy * 1000) / netGrandList;
}

/**
 * Choose "baseline mill rate" for comparison:
 * prefer CT Open Data, else stored/base.
 */
export function pickBaselineMillRate(ctMillRate, storedBaseMillRate) {
  const ctOk = typeof ctMillRate === "number" && Number.isFinite(ctMillRate);
  if (ctOk) return ctMillRate;

  const baseOk =
    typeof storedBaseMillRate === "number" && Number.isFinite(storedBaseMillRate);
  return baseOk ? storedBaseMillRate : null;
}

/**
 * Compute mill rate delta (workshop - baseline) with noise suppression.
 * Returns:
 * - null: cannot compute
 * - 0: effectively zero (suppressed)
 * - number: delta
 */
export function computeMillRateDelta(workshopMillRate, baselineMillRate) {
  const wOk =
    typeof workshopMillRate === "number" && Number.isFinite(workshopMillRate);
  const bOk =
    typeof baselineMillRate === "number" && Number.isFinite(baselineMillRate);

  if (!wOk || !bOk) return null;

  const d = workshopMillRate - baselineMillRate;
  if (!Number.isFinite(d) || Math.abs(d) < 0.005) return 0;

  return d;
}

/**
 * Parse user-entered currency-like values (e.g., "$1,234", "-5000") into a number.
 * Returns null for blank/invalid inputs.
 *
 * @param {unknown} raw
 * @returns {number|null}
 */
export function parseCurrencyLike(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;

  const cleaned = s.replace(/[^0-9.-]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * Pick a numeric total from the BoE rollup doc for a given year and preferred statuses.
 *
 * BoE rollup structure:
 * docData.years["2024"]["Approved"].Total
 *
 * @param {any} docData
 * @param {number} year
 * @param {string[]} preferredStatuses
 * @returns {number|null}
 */
export function pickBoeYearTotal(docData, year, preferredStatuses) {
  const y = docData?.years?.[String(year)];
  if (!y || typeof y !== "object") return null;

  for (const s of preferredStatuses || []) {
    const total = y?.[s]?.Total;
    if (typeof total === "number" && Number.isFinite(total)) return total;
  }

  return null;
}

/**
 * Pick a year+status block from a revenue document.
 *
 * Example:
 * docData["2024"]["Approved"] = { ...nested categories..., Total: 123 }
 *
 * @param {any} docData
 * @param {number} year
 * @param {string[]} preferredStatuses
 * @returns {object|null}
 */
export function pickYearStatusBlock(docData, year, preferredStatuses) {
  const y = docData?.[String(year)];
  if (!y || typeof y !== "object") return null;

  for (const s of preferredStatuses || []) {
    const block = y?.[s];
    if (block && typeof block === "object") return block;
  }

  return null;
}

/**
 * Collect leaf paths for all numeric values in a nested object (excluding "Total").
 *
 * @param {any} obj
 * @param {string[]=} prefix
 * @returns {string[][]}
 */
export function collectLeafPaths(obj, prefix = []) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return [];

  const out = [];

  for (const [k, v] of Object.entries(obj)) {
    if (k === "Total") continue;

    const next = [...prefix, k];

    if (v && typeof v === "object" && !Array.isArray(v)) {
      out.push(...collectLeafPaths(v, next));
    } else if (typeof v === "number" && Number.isFinite(v)) {
      out.push(next);
    }
  }

  return out;
}

/**
 * Safely read a numeric value at a nested path.
 *
 * @param {any} obj
 * @param {string[]} path
 * @returns {number|null}
 */
export function getAtPath(obj, path) {
  let cur = obj;
  for (const p of path || []) {
    if (!cur || typeof cur !== "object") return null;
    cur = cur[p];
  }
  return typeof cur === "number" && Number.isFinite(cur) ? cur : null;
}

/**
 * Build a display label for a revenue leaf path.
 *
 * @param {string} fullName
 * @param {string[]} path
 * @returns {{ labelNoCategory: string, labelWithCategory: string }}
 */
export function getRevenueDisplayLabel(fullName, path) {
  const toKey = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "")
      .trim();

  if (!Array.isArray(path) || path.length === 0) {
    return { labelNoCategory: "", labelWithCategory: "" };
  }

  const fullKey = toKey(fullName);
  const firstKey = toKey(path[0]);

  // Step 1: strip redundant wrapper matching the section title
  const strippedSection =
    fullKey && firstKey && fullKey === firstKey ? path.slice(1) : path;

  const safeSectionPath = strippedSection.length ? strippedSection : path;

  // "With category" label is whatever remains after stripping section wrapper.
  const labelWithCategory = safeSectionPath.join(" - ");

  // Step 2: Intergovernmental preferred display: drop the category bucket too (if present)
  const isIntergov = toKey(fullName) === toKey("Intergovernmental");

  let noCategoryPath = safeSectionPath;
  if (isIntergov && safeSectionPath.length > 1) {
    noCategoryPath = safeSectionPath.slice(1);
  }

  const labelNoCategory = (
    noCategoryPath.length ? noCategoryPath : safeSectionPath
  ).join(" - ");

  return { labelNoCategory, labelWithCategory };
}

/**
 * Build CommonTable rows for a single revenue Firestore document.
 *
 * Returns:
 * - A section header row: { section: fullName }
 * - Leaf rows only (NO section total row injected here)
 *
 * @param {any} docData
 * @param {string} fullName
 * @param {number} year
 * @returns {Array<object>}
 */
export function buildRevenueRowsFromDoc(docData, fullName, year) {
  const y2Block = pickYearStatusBlock(docData, year - 2, ["Actual"]);
  const y1Block = pickYearStatusBlock(docData, year - 1, [
    "Approved",
    "Adopted",
    "Actual",
  ]);
  const y0Block = pickYearStatusBlock(docData, year, [
    "Approved",
    "Adopted",
    "Actual",
  ]);

  const anyBlock = y2Block || y1Block || y0Block;

  // Always render the section header, even if empty.
  const rows = [{ section: fullName }];

  if (!anyBlock) {
    return rows;
  }

  // Union of leaf paths from all blocks (ignoring "Total")
  const leafPaths = [];
  const pushUnique = (paths) => {
    for (const p of paths) {
      const key = p.join("\u0001");
      if (!leafPaths.some((x) => x.join("\u0001") === key)) leafPaths.push(p);
    }
  };

  pushUnique(collectLeafPaths(y2Block || {}));
  pushUnique(collectLeafPaths(y1Block || {}));
  pushUnique(collectLeafPaths(y0Block || {}));

  // Build label candidates up-front for de-duping.
  const labelMeta = leafPaths.map((path) => {
    const { labelNoCategory, labelWithCategory } = getRevenueDisplayLabel(
      fullName,
      path
    );
    return { path, labelNoCategory, labelWithCategory };
  });

  // Detect duplicates in the preferred label (no-category).
  const counts = new Map();
  for (const m of labelMeta) {
    const key = m.labelNoCategory;
    counts.set(key, (counts.get(key) || 0) + 1);
  }

  // Final label: if preferred label collides, fall back to with-category label.
  const finalMeta = labelMeta.map((m) => {
    const isDup = (counts.get(m.labelNoCategory) || 0) > 1;
    return {
      ...m,
      finalLabel: isDup ? m.labelWithCategory : m.labelNoCategory,
    };
  });

  // Sort by displayed label
  finalMeta.sort((a, b) => a.finalLabel.localeCompare(b.finalLabel));

  for (const { path, finalLabel } of finalMeta) {
    rows.push({
      label: finalLabel,
      y2_actual: getAtPath(y2Block, path),
      y1_adopted: getAtPath(y1Block, path),
      y0_adopted: getAtPath(y0Block, path),
      buildBudget: undefined,
    });
  }

  return rows;
}

/**
 * Inject a blank-label section subtotal row at the end of each revenue section.
 * Mirrors the expenditures style where the subtotal row is visually “the total line”.
 *
 * @param {Array<any>} rows
 * @returns {Array<any>}
 */
export function injectRevenueSectionTotals(rows) {
  const isFiniteNumber = (v) => typeof v === "number" && Number.isFinite(v);

  const out = [];

  let currentSection = null;
  let sums = { y2_actual: 0, y1_adopted: 0, y0_adopted: 0, buildBudget: 0 };
  let sawAnyLeaf = false;

  const flushSectionTotal = () => {
    if (!currentSection) return;

    out.push({
      label: "",
      y2_actual: sawAnyLeaf ? sums.y2_actual : 0,
      y1_adopted: sawAnyLeaf ? sums.y1_adopted : 0,
      y0_adopted: sawAnyLeaf ? sums.y0_adopted : 0,
      buildBudget: sawAnyLeaf ? sums.buildBudget : 0,
      bold: true,
      isTotal: true,
      isSectionTotal: true,
      readOnly: true,
      disableEdit: true,
      aggregate: true,
      totalForSection: currentSection,
    });

    currentSection = null;
    sums = { y2_actual: 0, y1_adopted: 0, y0_adopted: 0, buildBudget: 0 };
    sawAnyLeaf = false;
  };

  for (const row of rows || []) {
    if (row?.section) {
      flushSectionTotal();
      currentSection = row.section;
      out.push(row);
      continue;
    }

    if (
      typeof row?.label === "string" &&
      row.label.toUpperCase() === "TOTAL REVENUES"
    ) {
      flushSectionTotal();
      out.push(row);
      continue;
    }

    out.push(row);

    if (!currentSection) continue;

    const isAggregateRow =
      row?.isTotal ||
      row?.isSectionTotal ||
      row?.aggregate ||
      (typeof row?.label === "string" &&
        row.label.toUpperCase().startsWith("TOTAL "));
    if (isAggregateRow) continue;

    if (isFiniteNumber(row?.y2_actual)) sums.y2_actual += row.y2_actual;
    if (isFiniteNumber(row?.y1_adopted)) sums.y1_adopted += row.y1_adopted;
    if (isFiniteNumber(row?.y0_adopted)) sums.y0_adopted += row.y0_adopted;
    if (isFiniteNumber(row?.buildBudget)) sums.buildBudget += row.buildBudget;

    if (
      isFiniteNumber(row?.y2_actual) ||
      isFiniteNumber(row?.y1_adopted) ||
      isFiniteNumber(row?.y0_adopted) ||
      isFiniteNumber(row?.buildBudget)
    ) {
      sawAnyLeaf = true;
    }
  }

  flushSectionTotal();
  return out;
}
